import type {
  ArbitraryTypedObject,
  PortableTextBlock,
  TypedObject,
} from '@portabletext/types';
import Link from 'next/link';
import {
  PortableText,
  type PortableTextMarkComponentProps,
  type PortableTextTypeComponent,
} from 'next-sanity';
import cn from '@/lib/cn';

type Block = (TypedObject & PortableTextBlock) | ArbitraryTypedObject;

export default function RichText({
  className = undefined,
  componentsTypes = {},
  value,
}: {
  /** Additional CSS class(es) for the button */
  className?: string;
  /** Custom components for Portable Text types */
  componentsTypes?: Record<string, PortableTextTypeComponent>;
  /** The Portable Text value to render */
  value: Block | Block[];
}) {
  const components = {
    marks: {
      link: ({ children, value }: PortableTextMarkComponentProps) => {
        if (!value?.href) return children;

        const isInternal =
          value.href.includes('://about.chrono24.com') ||
          value.href.startsWith('/');

        const linkClass =
          'hover:text-neutral-30 underline transition-colors hover:no-underline';

        if (isInternal) {
          return (
            <Link
              className={linkClass}
              href={value.href.replace(/https?:\/\/about\.chrono24\.com/g, '')}
            >
              {children}
            </Link>
          );
        }

        return (
          <a
            className={linkClass}
            href={value.href}
            target="_blank"
            rel="noreferrer noopener"
          >
            {children}
          </a>
        );
      },
    },
    types: componentsTypes,
  };

  return (
    <div
      className={cn(
        'text-neutral-10/80 [&>*]:first:mt-0', // general
        '[&>h2]:text-neutral-10 [&>h2]:text-md [&>h2]:mt-10 [&>h2]:font-semibold md:[&>h2]:text-lg', // headings
        '[&>p]:mt-5 md:[&>p]:mt-6', // paragraphs
        '[&_ol,&_ul]:mt-5', // lists
        '[&_li]:before:bg-deep-blue-90 [&_li]:before:text-deep-blue-20 [&_li]:relative [&_li]:mt-5 [&_li]:pl-9 [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:flex [&_li]:before:size-6 [&_li]:before:shrink-0 [&_li]:before:items-center [&_li]:before:justify-center [&_li]:before:rounded-xs [&_li]:first:mt-0', // list items
        '[&_ol]:[counter-reset:list-item] [&_ol>li]:[counter-increment:list-item] [&_ol>li]:before:font-medium [&_ol>li]:before:content-[counter(list-item)]', // ordered lists
        '[&_ul>li]:before:content-(--list-item-checkmark)', // unordered lists
        className,
      )}
    >
      <PortableText
        value={value}
        components={components}
      />
    </div>
  );
}
